// Terms of Service — plain, factual language covering the booking, payment,
// cancellation and liability terms for a Greek pre-booked taxi service.

import { CONTACT } from "@/data/site";

const EFFECTIVE_DATE = "10 July 2026";

const Terms = () => {
  return (
    <main
      data-testid="terms-page"
      className="max-w-3xl mx-auto px-5 lg:px-10 pt-28 pb-24 text-foreground/85"
    >
      <h1 className="font-display text-4xl md:text-5xl text-gold mb-2">Terms of Service</h1>
      <p className="text-xs uppercase tracking-[0.18em] text-foreground/50 mb-10">
        Effective: {EFFECTIVE_DATE}
      </p>

      <section className="space-y-4 text-sm leading-relaxed">
        <p>
          These Terms govern the use of{" "}
          <a
            className="text-gold underline hover:no-underline"
            href="https://athensairporttaxitransfers.com"
          >
            athensairporttaxitransfers.com
          </a>{" "}
          and every booking made through it. By confirming a booking you agree
          to these Terms.
        </p>
      </section>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">1. The service</h2>
      <p className="text-sm">
        We are a licensed private-taxi transfer service operating in Attica,
        Greece. Every transfer is carried out by a professionally licensed
        driver in a fully insured vehicle. The site allows you to book fixed-
        price airport / port transfers and custom point-to-point rides in
        advance.
      </p>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">2. Booking &amp; pricing</h2>
      <ul className="list-disc list-outside pl-5 space-y-2 text-sm">
        <li>
          Prices shown at booking are per vehicle (up to 4 passengers) and
          include tolls, VAT, taxi fare and one meet-and-greet.
        </li>
        <li>
          A night surcharge applies between 22:30 and 05:00. This is calculated
          automatically before you pay.
        </li>
        <li>
          Custom point-to-point rides outside the fixed-rate zones are priced
          on distance and time.
        </li>
      </ul>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">3. Payment</h2>
      <p className="text-sm">
        Payment is processed securely by Stripe in Euro (EUR). A booking is
        confirmed only after successful payment; the confirmation email
        contains your booking reference (PT-XXXXXX). If Stripe declines the
        card, the booking is not created and no charge is made.
      </p>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">4. Cancellation &amp; refunds</h2>
      <ul className="list-disc list-outside pl-5 space-y-2 text-sm">
        <li>
          Cancellations received <strong>more than 12 hours</strong> before the
          scheduled pickup are refunded in full.
        </li>
        <li>
          Cancellations received <strong>less than 12 hours</strong> before
          pickup, or no-shows, are non-refundable, since the driver has
          already been dispatched or scheduled.
        </li>
        <li>
          To cancel, email{" "}
          <a className="text-gold underline hover:no-underline" href={`mailto:${CONTACT.email}`}>
            {CONTACT.email}
          </a>{" "}
          or WhatsApp{" "}
          <a
            className="text-gold underline hover:no-underline"
            href={`https://wa.me/${CONTACT.phoneRaw}`}
            target="_blank"
            rel="noreferrer"
          >
            {CONTACT.phone}
          </a>{" "}
          with your booking reference.
        </li>
      </ul>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">5. Changes to your booking</h2>
      <p className="text-sm">
        You may change the pickup date, time or drop-off address free of
        charge up to 12 hours before pickup, subject to driver availability.
        Contact us using the channels above.
      </p>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">6. Delays &amp; missed pickups</h2>
      <p className="text-sm">
        The driver will wait for you up to 60 minutes at the airport arrivals
        hall or up to 15 minutes at any other pickup point, free of charge.
        Beyond that, an extra waiting fee of €0.50 per minute may apply. If
        your flight is delayed, we track the flight and adjust the pickup
        automatically at no extra cost when a flight number is provided.
      </p>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">7. Liability</h2>
      <p className="text-sm">
        Our liability is limited to the amount paid for the booking. We are
        not liable for missed flights, missed ferries or connections caused by
        events beyond our reasonable control (traffic accidents, road
        closures, strikes, extreme weather). Every vehicle carries full third-
        party and passenger insurance as required by Greek law.
      </p>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">8. Governing law</h2>
      <p className="text-sm">
        These Terms are governed by Greek law. Any dispute that cannot be
        resolved amicably is subject to the exclusive jurisdiction of the
        courts of Athens, Greece.
      </p>

      <h2 className="font-display text-2xl text-gold mt-10 mb-3">9. Contact</h2>
      <p className="text-sm">
        Athens Airport Taxi Transfers · {CONTACT.address}
        <br />
        Email:{" "}
        <a className="text-gold underline hover:no-underline" href={`mailto:${CONTACT.email}`}>
          {CONTACT.email}
        </a>
        <br />
        Phone:{" "}
        <a className="text-gold underline hover:no-underline" href={`tel:+${CONTACT.phoneRaw}`}>
          {CONTACT.phone}
        </a>
      </p>
    </main>
  );
};

export default Terms;
